import useDeclarations from 'hooks/useDeclarations';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { goToDeclarationField } from 'views/declarations/utils/form-utils';
import { getDeclarationUrl } from 'views/declarations/utils/getDeclarationUrl';
import { requestFieldReveal } from 'views/declarations/utils/revealDeclarationField';
import isBox44Path from './error-cards/isBox44Path';
import { ErrorCardContext } from './error-cards/types';
import { ResolvedErrorField } from './resolveErrorField';

/** Long enough for the page navigated to to render before the field is looked for. */
const AFTER_NAVIGATION_DELAY_MS = 300;

interface Options {
    onClose?: () => void;
    /** Rewrites a path the form names differently from the error, e.g. CDS dangerous goods and agent. */
    toFormFieldName?: (formPath: string) => string;
    /** Where Box 44 lives on the declaration and on an item - a field in it is reached through its modal. */
    box44Paths?: ErrorCardContext['box44Paths'];
}

/**
 * Takes the user to the field an error names: into its item - found by id, so it is the right one
 * however the products table is sorted, searched or paged - or to the declaration. A declaration
 * opened from a manifest stays inside the manifest.
 *
 * Box 44 is edited in a modal rather than a card, so a field in it opens the modal instead of being
 * scrolled to.
 */
const useGoToErrorField = ({ onClose, toFormFieldName, box44Paths }: Options = {}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { manifestId, jobId, declarationId: routeDeclarationId } = useParams();
    const { declaration } = useDeclarations();

    return ({ item, formPath }: ResolvedErrorField) => {
        const basePath = getDeclarationUrl({ manifestId, jobId, declarationId: routeDeclarationId ?? declaration?.id });
        const viewOnlyPath = location.pathname.includes('view-only') ? '/view-only' : '';

        if (item) {
            // An item deleted since the rejection has nowhere to go, but the drawer still gets out of
            // the way rather than swallowing the click.
            if (!item.itemId || !item.itemNumberToday) {
                onClose?.();
                return;
            }
            navigate(`${basePath}${viewOnlyPath}/products/${item.itemId}`);
        } else if (location.pathname.split('/').at(-1) !== declaration?.id) {
            navigate(`${basePath}${viewOnlyPath}`);
        }

        onClose?.();

        const box44Path = item ? box44Paths?.productPath : box44Paths?.masterPath;
        if (isBox44Path(formPath, box44Path)) {
            // The modal edits the Box 44 of the page it is opened on, so it waits for the navigation.
            setTimeout(() => requestFieldReveal(formPath), AFTER_NAVIGATION_DELAY_MS);
            return;
        }

        goToDeclarationField(toFormFieldName ? toFormFieldName(formPath) : formPath, AFTER_NAVIGATION_DELAY_MS);
    };
};

export default useGoToErrorField;
